import { useState } from "react";
import { Building2, BadgePercent, ShieldCheck, Palette } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Reveal, Eyebrow } from "../components/Reveal";

const ADVANTAGES = [
  {
    icon: Building2,
    title: "Accès promoteur direct",
    text: "Grâce à notre réseau de promoteurs partenaires, vous accédez aux programmes avant leur commercialisation grand public — aux conditions promoteur, sans frais d'agence.",
  },
  {
    icon: BadgePercent,
    title: "Frais de notaire réduits",
    text: "Dans le neuf, les frais de notaire tombent à environ 2 à 3 % du prix, contre 7 à 8 % dans l'ancien. Une économie réelle dès la signature.",
  },
  {
    icon: ShieldCheck,
    title: "Garanties constructeur",
    text: "Garantie décennale, garantie de parfait achèvement, livraison à prix et délais convenus : votre achat est sécurisé par le cadre légal du neuf.",
  },
  {
    icon: Palette,
    title: "Personnalisation & RE2020",
    text: "Choix des matériaux et des aménagements sur plan, performances énergétiques aux dernières normes : un bien neuf, à votre image et économe.",
  },
];

const STEPS = [
  {
    n: "01",
    title: "Parlez-nous de votre projet",
    text: "Secteur, budget, type de bien, délais : nous définissons ensemble votre cahier des charges.",
  },
  {
    n: "02",
    title: "Accès prioritaire aux lancements",
    text: "Dès qu'un programme de nos promoteurs partenaires correspond à vos critères, vous êtes informé en avant-première.",
  },
  {
    n: "03",
    title: "Réservation accompagnée",
    text: "Négociation, contrat de réservation, suivi de chantier et coordination notariale jusqu'à la remise des clés.",
  },
];

const fieldCls =
  "w-full rounded-xl border border-slate-200 bg-white px-4 py-3.5 text-sm font-light text-azur outline-none transition-colors focus:border-azur";

export default function Neufs() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", secteur: "", budget: "", type_bien: "", message: "",
  });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/leads", {
        kind: "neuf",
        name: form.name,
        email: form.email,
        phone: form.phone,
        message: form.message,
        details: { secteur: form.secteur, budget: form.budget, type_bien: form.type_bien },
      });
      toast.success("Inscription enregistrée. Vous serez informé en avant-première des prochains lancements.");
      setForm({ name: "", email: "", phone: "", secteur: "", budget: "", type_bien: "", message: "" });
    } catch {
      toast.error("Une erreur est survenue, veuillez réessayer.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <PageHeader
        eyebrow="Programmes neufs"
        title="Le neuf, en accès privilégié."
        sub="Grâce à notre réseau de promoteurs partenaires sur la Côte d'Azur, accédez aux programmes neufs avant tout le monde — aux conditions promoteur."
      />

      <section data-testid="neufs-page" className="mx-auto max-w-7xl px-4 py-16 sm:px-8 lg:px-16 lg:py-24">
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {ADVANTAGES.map((a, i) => (
            <Reveal key={a.title} delay={i * 0.08}>
              <div className="group h-full rounded-2xl border border-slate-200/80 bg-white p-7 shadow-[0_4px_20px_rgba(0,0,0,0.03)] transition-all duration-500 hover:-translate-y-1.5 hover:shadow-[0_12px_40px_rgba(12,35,64,0.08)]">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-azur text-sand transition-colors duration-500 group-hover:bg-terracotta">
                  <a.icon size={19} />
                </span>
                <h3 className="mt-5 font-serif text-xl font-semibold text-azur">{a.title}</h3>
                <p className="mt-2 text-sm font-light leading-relaxed text-slate-600">{a.text}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="mt-24 grid gap-12 lg:grid-cols-2 lg:gap-20">
          <div>
            <Reveal>
              <Eyebrow>Comment ça marche</Eyebrow>
              <h2 className="mt-4 font-serif text-2xl font-normal tracking-tight text-azur sm:text-3xl">
                De l'avant-première à la remise des clés.
              </h2>
            </Reveal>
            <div className="mt-8">
              {STEPS.map((s, i) => (
                <Reveal key={s.n} delay={i * 0.08}>
                  <div className="group flex gap-6 border-t border-slate-200 py-7 transition-colors duration-500 hover:border-gold">
                    <span className="outline-number font-serif text-4xl font-light">{s.n}</span>
                    <div>
                      <h3 className="font-serif text-xl font-semibold text-azur">{s.title}</h3>
                      <p className="mt-1.5 text-sm font-light leading-relaxed text-slate-600">{s.text}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.2}>
              <p className="mt-6 font-serif text-lg italic text-azur/70">
                « Nos promoteurs partenaires nous ouvrent leurs lancements avant le grand public — discrétion et priorité garanties. »
              </p>
            </Reveal>
          </div>

          <Reveal delay={0.15}>
            <form
              onSubmit={submit}
              data-testid="neufs-form"
              className="rounded-2xl border border-slate-200/80 bg-white p-8 shadow-[0_4px_20px_rgba(0,0,0,0.03)] lg:sticky lg:top-28"
            >
              <span className="font-mono text-xs font-medium uppercase tracking-[0.25em] text-terracotta">
                Liste prioritaire
              </span>
              <h2 className="mt-3 font-serif text-2xl font-semibold text-azur">
                Être informé des prochains lancements.
              </h2>
              <div className="mt-6 grid gap-4 sm:grid-cols-2">
                <input required placeholder="Nom et prénom" value={form.name} onChange={set("name")} data-testid="neufs-name" className={fieldCls} />
                <input required type="email" placeholder="Email" value={form.email} onChange={set("email")} data-testid="neufs-email" className={fieldCls} />
                <input placeholder="Téléphone" value={form.phone} onChange={set("phone")} data-testid="neufs-phone" className={fieldCls} />
                <input placeholder="Secteur recherché (Nice, Cannes…)" value={form.secteur} onChange={set("secteur")} data-testid="neufs-sector" className={fieldCls} />
                <select value={form.type_bien} onChange={set("type_bien")} data-testid="neufs-type" className={fieldCls}>
                  <option value="">Type de bien</option>
                  <option value="Appartement">Appartement</option>
                  <option value="Villa">Villa</option>
                  <option value="Indifférent">Indifférent</option>
                </select>
                <select value={form.budget} onChange={set("budget")} data-testid="neufs-budget" className={fieldCls}>
                  <option value="">Budget envisagé</option>
                  <option value="< 500 000 €">Moins de 500 000 €</option>
                  <option value="500 000 – 1 M€">500 000 – 1 M€</option>
                  <option value="1 – 3 M€">1 – 3 M€</option>
                  <option value="> 3 M€">Plus de 3 M€</option>
                </select>
              </div>
              <textarea
                rows={4}
                placeholder="Votre projet en quelques mots (résidence principale, investissement, délais…)"
                value={form.message}
                onChange={set("message")}
                data-testid="neufs-message"
                className={fieldCls + " mt-4 resize-none"}
              />
              <button
                type="submit"
                disabled={sending}
                data-testid="neufs-submit"
                className="mt-6 w-full rounded-xl bg-azur px-6 py-4 text-sm font-medium text-sand transition-colors duration-300 hover:bg-terracotta disabled:opacity-60"
              >
                {sending ? "Envoi en cours…" : "Rejoindre la liste prioritaire"}
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </>
  );
}
